import React from 'react';
import { RMA_STAGES, RMA_STAGE, stageOf } from './rmaHelpers';

// Horizontal progress strip for the detail screen — one column per RMA_STAGES entry.
//
// It reads as a history of events, so the stages use timelineLabel ("Raised", "Dispatched")
// rather than the state wording the queue pills use ("Awaiting dispatch", "With
// manufacturer"). Same stage keys either way — only the register differs.
//
// metaFor keys off stage.key, not step number: the steps were renumbered when the
// in_transit stage was retired, and a positional switch silently attaches the wrong
// caption to every stage after the one that moved.
function RmaStageTimeline({ shipment, now = Date.now() }) {
  const current = RMA_STAGE[stageOf(shipment)];
  const currentStep = current?.step || 1;

  const metaFor = (stage) => {
    const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
    if (stage.key === 'awaiting_dispatch') {
      const d = fmt(shipment.createdAt);
      return d ? d + (shipment.createdBy ? ' · ' + shipment.createdBy : '') : 'not yet';
    }
    // Dispatch and time-at-manufacturer used to be two columns. Now they are one, so this
    // shows the dispatch date and carrier while away, and the SLA position underneath it is
    // carried by the queue's own overdue pill.
    if (stage.key === 'with_manufacturer') {
      if (!shipment.dispatchedAt) return 'not yet';
      if (stage.step < currentStep) return fmt(shipment.returnedAt) || 'complete';
      const days = Math.max(0, Math.floor((now - new Date(shipment.dispatchedAt)) / 86400000));
      const sla = shipment.slaDays ?? 30;
      return 'day ' + days + ' of ' + sla;
    }
    if (stage.key === 'returned_to_bench') return fmt(shipment.returnedAt) || 'not yet';
    return fmt(shipment.closedAt) || 'not yet';
  };

  return (
    <div style={{
      background: 'var(--surface-default)', border: '1px solid var(--border-default)',
      borderRadius: 'var(--radius-xl)', padding: '20px 24px',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${RMA_STAGES.length},1fr)`, gap: 0 }}>
        {RMA_STAGES.map((s, i) => {
          const done = s.step < currentStep;
          const isCurrent = s.step === currentStep;
          const last = i === RMA_STAGES.length - 1;
          return (
            <div key={s.key} style={{ position: 'relative', paddingRight: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                <span style={{
                  width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                  background: done ? 'var(--brand-default)' : 'var(--surface-default)',
                  border: done || isCurrent ? '3px solid var(--brand-default)' : '2px solid var(--border-hover)',
                }} />
                {!last && (
                  <span style={{
                    flex: 1, height: 2,
                    background: done ? 'var(--brand-border)' : 'var(--border-default)',
                  }} />
                )}
              </div>
              <div style={{
                fontSize: 12.5, fontWeight: 600,
                color: done || isCurrent ? 'var(--text-primary)' : 'var(--text-ghost)',
              }}>{s.timelineLabel}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-ghost)', marginTop: 3 }}>{metaFor(s)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RmaStageTimeline;
